import {
    Component, Input, Output, EventEmitter,
    HostListener, ElementRef, OnInit, OnDestroy, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';

export interface InactiveTimeOption {
    value: string;
    label: string;
}

export const INACTIVE_TIME_OPTIONS: InactiveTimeOption[] = [
    { value: '10min', label: '10 min' },
    { value: '20min', label: '20 min' },
    { value: '30min', label: '30 min' },
    { value: '1h', label: '1 hora' },
    { value: '2h', label: '2 horas' },
    { value: '3h', label: '3 horas' },
];

@Component({
    selector: 'klab-inactive-time-dropdown',
    standalone: false,
    templateUrl: './inactive-time-dropdown.component.html',
    styleUrls: ['./inactive-time-dropdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class InactiveTimeDropdownComponent implements OnInit, OnDestroy {
    @ViewChild('connectedDrop', { static: false }) connectedDrop?: CdkConnectedOverlay;

    @Input() value: string = '10min';
    @Input() readOnly = false;
    // Cuando se define, habilita el estado "sin asignar" (valor vacio ''): se
    // agrega una opcion con este label y NO se fuerza el default '10min'.
    @Input() unsetLabel?: string;
    @Output() valueChange = new EventEmitter<string>();

    options = INACTIVE_TIME_OPTIONS;
    isOpen = false;
    panelMaxHeight = 220;
    overlayWidth = 0;

    private ignoreNextDocDown = false;
    private readonly preferredMaxHeight = 220;
    private readonly minHeight = 120;
    private readonly viewportBuffer = 8;
    private overlayFollowTeardown?: () => void;
    private overlayPositionRaf = 0;

    readonly overlayScrollStrategy: ScrollStrategy;
    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
    ];
    readonly overlayPanelClass = 'klab-itd-overlay-pane';

    constructor(
        private elRef: ElementRef,
        private overlay: Overlay,
    ) {
        this.overlayScrollStrategy = this.overlay.scrollStrategies.reposition({ scrollThrottle: 16 });
    }

    ngOnInit(): void {
        if (this.unsetLabel != null) {
            this.options = [{ value: '', label: this.unsetLabel }, ...INACTIVE_TIME_OPTIONS];
        }
        if (!this.value && this.unsetLabel == null) this.value = '10min';
    }

    ngOnDestroy(): void {
        this.detachFollowListeners();
    }

    get selectedLabel(): string {
        return this.options.find(o => o.value === this.value)?.label ?? (this.unsetLabel ?? '10 min');
    }

    toggle(): void {
        if (this.readOnly) return;
        if (!this.isOpen) {
            this.computeGeometry();
            this.isOpen = true;
            this.attachFollowListeners();
            setTimeout(() => this.scheduleGeometrySync(), 0);
        } else {
            this.close();
        }
    }

    close(): void {
        this.detachFollowListeners();
        this.isOpen = false;
    }

    onOverlayDetach(): void {
        if (this.isOpen) this.close();
    }

    onPanelMouseDown(): void {
        this.ignoreNextDocDown = true;
        queueMicrotask(() => (this.ignoreNextDocDown = false));
    }

    select(opt: InactiveTimeOption): void {
        this.value = opt.value;
        this.valueChange.emit(opt.value);
        this.close();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(ev: MouseEvent): void {
        if (!this.isOpen) return;
        if (this.ignoreNextDocDown) return;
        const t = ev.target as Node;
        if ((this.elRef.nativeElement as HTMLElement).contains(t)) return;
        const pane = document.querySelector(`.${this.overlayPanelClass}`);
        if (pane?.contains(t)) return;
        this.close();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        if (!this.isOpen) return;
        this.scheduleGeometrySync();
    }

    private computeGeometry(): void {
        const rect = (this.elRef.nativeElement as HTMLElement).getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openUp = spaceBelow < this.preferredMaxHeight + this.viewportBuffer && spaceAbove > spaceBelow;
        const sideSpace = openUp ? spaceAbove : spaceBelow;
        this.panelMaxHeight = Math.max(this.minHeight, Math.min(this.preferredMaxHeight, Math.floor(sideSpace - this.viewportBuffer)));
        this.overlayWidth = Math.max(rect.width, 130);
    }

    private scheduleGeometrySync(): void {
        if (!this.isOpen) return;
        this.computeGeometry();
        this.connectedDrop?.overlayRef?.updatePosition();
        queueMicrotask(() => {
            requestAnimationFrame(() => {
                this.computeGeometry();
                this.connectedDrop?.overlayRef?.updatePosition();
            });
        });
    }

    private queuePositionSync(): void {
        if (!this.isOpen || this.overlayPositionRaf) return;
        this.overlayPositionRaf = requestAnimationFrame(() => {
            this.overlayPositionRaf = 0;
            this.computeGeometry();
            this.connectedDrop?.overlayRef?.updatePosition();
        });
    }

    private attachFollowListeners(): void {
        this.detachFollowListeners();
        const cleanups: Array<() => void> = [];
        const onFollow = () => this.queuePositionSync();

        const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
        if (vv) {
            vv.addEventListener('resize', onFollow);
            vv.addEventListener('scroll', onFollow);
            cleanups.push(() => { vv.removeEventListener('resize', onFollow); vv.removeEventListener('scroll', onFollow); });
        }

        if (typeof document !== 'undefined') {
            document.addEventListener('scroll', onFollow, true);
            cleanups.push(() => document.removeEventListener('scroll', onFollow, true));
        }

        this.overlayFollowTeardown = () => {
            cleanups.forEach(fn => fn());
            if (this.overlayPositionRaf) { cancelAnimationFrame(this.overlayPositionRaf); this.overlayPositionRaf = 0; }
            this.overlayFollowTeardown = undefined;
        };
    }

    private detachFollowListeners(): void {
        this.overlayFollowTeardown?.();
    }
}
